import type { License } from '../license/types.js';
import { OPT_IN_FEATURES } from '../schema/features.js';
import type { FeatureFlag } from '../schema/features.js';
import type { MobiLauncherConfig } from '../schema/index.js';
import { TIER_FEATURES } from './features.js';

/**
 * Compute the effective feature set:
 *
 *   entitled  = TIER_FEATURES[license.tier] ∪ license.features
 *   effective = enabledFeatures.length === 0
 *                 ? entitled
 *                 : entitled ∩ enabledFeatures
 *
 * If `license` is null (no license, invalid signature, expired, or tenant
 * mismatch — the caller has already collapsed all those into null), the
 * device runs as Core. Core's tier defaults are empty, so the effective set
 * is empty unless explicit `license.features` grants exist (which they
 * cannot, when license is null).
 *
 * **Empty `enabledFeatures` means "use everything the license entitles"** —
 * EXCEPT the opt-in-only features (see {@link OPT_IN_FEATURES}). This is the
 * typical case: customers don't think about feature flags, they think about
 * features. Forcing them to enumerate every flag to "turn on Pro" was a UX
 * trap — paid customers saw nothing change after upgrade because the
 * intersection with `[]` collapsed to nothing. But opt-in modes like
 * `signageMode` (which hides the launcher chrome) must NOT come along for the
 * ride — only an explicit `enabledFeatures` entry activates them.
 *
 * **Non-empty `enabledFeatures` is an explicit allow-list** — the customer
 * is deliberately opting IN to a subset. Still intersected with the
 * license entitlements so a Free tenant can't grant themselves Pro features
 * by editing the JSON. The Builder enforces "locked" UX for non-entitled
 * fields; this is the runtime backstop.
 */
export function resolveFeatures(
  config: MobiLauncherConfig,
  license: License | null,
): Set<FeatureFlag> {
  const tier = license?.tier ?? 'core';
  const tierDefaults = TIER_FEATURES[tier];
  const grants = license?.features ?? [];

  const entitled = new Set<FeatureFlag>(tierDefaults);
  for (const f of grants) entitled.add(f);

  if (config.enabledFeatures.length === 0) {
    // "Everything entitled" — minus the opt-in-only modes, which stay off
    // until explicitly listed (a tenant who never asked for signage mustn't
    // get their header/footer hidden just by upgrading).
    for (const f of OPT_IN_FEATURES) entitled.delete(f);
    return entitled;
  }

  // Explicit allow-list: intersect with entitlements. An explicit opt-in
  // feature (e.g. signageMode) listed here AND entitled is honored — this is
  // the only path that turns one on.
  const effective = new Set<FeatureFlag>();
  for (const f of config.enabledFeatures) {
    if (entitled.has(f)) effective.add(f);
  }
  return effective;
}
